/* ═══════════════════════════════════════════════════════════════════════════
   KT Cart Drawer Extras
   ---------------------------------------------------------------------------
   Prestige's <cart-drawer> re-renders this section server-side on every cart
   change (cart:change / cart:refresh). That means the progress bar, promo text,
   shipping and totals are pure Liquid and need NO JS to stay in sync.

   This file only handles the two interactive pieces:
     1. Reviews carousel — a self-contained, dependency-free auto-slider.
     2. Add-on quick-add  — a size-pill popup that adds via /cart/add.js and
        dispatches `cart:refresh` so Prestige rebuilds the drawer.

   Everything is delegated on `document` and re-initialized on cart refresh, so
   it survives Prestige replacing the cart-drawer DOM.
═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ── Reviews Carousel ───────────────────────────────────────────────────── */
  var reviewTimers = new WeakMap();

  function initReviews(root) {
    var reviews = (root || document).querySelector('[data-cd-reviews]');
    if (!reviews || reviews.__ktInit) return;

    var inner = reviews.querySelector('.cd-reviews-inner');
    var track = reviews.querySelector('[data-cd-reviews-track]');
    var slides = track ? track.querySelectorAll('[data-cd-slide]') : [];
    var dotsWrap = reviews.querySelector('[data-cd-dots]');
    if (!track || slides.length === 0) return;

    reviews.__ktInit = true;
    reviews.classList.add('cd-reviews--ready');

    // Convert the static (display:none) slides into a sliding track.
    if (track) {
      track.classList.add('cd-reviews-track');
      Array.prototype.forEach.call(slides, function (s) {
        s.classList.remove('cd-review-slide--active');
      });
    }

    var index = 0;
    var count = slides.length;

    // Build dots
    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cd-review-dot' + (i === 0 ? ' is-selected' : '');
        dot.setAttribute('aria-label', 'Go to review ' + (i + 1));
        dot.dataset.idx = String(i);
        dotsWrap.appendChild(dot);
      }
    }

    function go(i) {
      index = (i + count) % count;
      track.style.transform = 'translateX(-' + (index * 100) + '%)';
      if (dotsWrap) {
        var dots = dotsWrap.querySelectorAll('.cd-review-dot');
        Array.prototype.forEach.call(dots, function (d, di) {
          d.classList.toggle('is-selected', di === index);
        });
      }
    }

    function start() {
      stop();
      if (count <= 1) return;
      var t = setInterval(function () { go(index + 1); }, 3500);
      reviewTimers.set(reviews, t);
    }
    function stop() {
      var t = reviewTimers.get(reviews);
      if (t) { clearInterval(t); reviewTimers.delete(reviews); }
    }

    if (dotsWrap) {
      dotsWrap.addEventListener('click', function (e) {
        var dot = e.target.closest('.cd-review-dot');
        if (!dot) return;
        go(parseInt(dot.dataset.idx, 10));
        start(); // restart timer after manual nav
      });
    }

    // Pause when the tab/drawer is hidden is unnecessary; keep it simple.
    if (inner) {
      inner.addEventListener('mouseenter', stop);
      inner.addEventListener('mouseleave', start);
    }

    /* ── Drag / swipe (pointer events cover mouse + touch) ─────────────────── */
    if (count > 1 && inner) {
      var dragging = false;
      var startX = 0;
      var deltaX = 0;
      var width = 0;
      var pointerId = null;

      function onDown(e) {
        // Ignore non-primary buttons / multi-touch starts.
        if (e.button != null && e.button !== 0) return;
        dragging = true;
        pointerId = e.pointerId;
        startX = e.clientX;
        deltaX = 0;
        width = inner.getBoundingClientRect().width || 1;
        track.classList.add('cd-reviews-track--dragging');
        stop(); // pause autoplay while interacting
        if (e.pointerId != null && inner.setPointerCapture) {
          try { inner.setPointerCapture(e.pointerId); } catch (err) {}
        }
      }

      function onMove(e) {
        if (!dragging) return;
        deltaX = e.clientX - startX;
        var pct = (deltaX / width) * 100;
        track.style.transform = 'translateX(calc(-' + (index * 100) + '% + ' + pct + '%))';
      }

      function onUp() {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('cd-reviews-track--dragging');
        if (pointerId != null && inner.releasePointerCapture) {
          try { inner.releasePointerCapture(pointerId); } catch (err) {}
        }
        // Snap: if dragged more than 1/5 of the width, advance/retreat.
        var threshold = width * 0.2;
        if (deltaX <= -threshold) {
          go(index + 1);
        } else if (deltaX >= threshold) {
          go(index - 1);
        } else {
          go(index); // snap back
        }
        deltaX = 0;
        start();
      }

      inner.addEventListener('pointerdown', onDown);
      inner.addEventListener('pointermove', onMove);
      inner.addEventListener('pointerup', onUp);
      inner.addEventListener('pointercancel', onUp);
      inner.addEventListener('pointerleave', onUp);
      // Prevent the browser from treating a horizontal drag as a text selection.
      inner.addEventListener('dragstart', function (e) { e.preventDefault(); });
    }

    go(0);
    start();
  }

  /* ── Init + re-init on cart refresh ─────────────────────────────────────── */
  function refresh() {
    // Reviews root is replaced on re-render, so the __ktInit flag resets with it.
    initReviews(document);
  }

  function init() {
    initReviews(document);

    // Prestige replaces the cart-drawer section content on these events.
    ['cart:refresh', 'cart:change', 'cart-drawer:refreshed', 'theme:cart:updated'].forEach(function (evt) {
      document.addEventListener(evt, function () {
        // Defer so the new DOM is in place before we wire it.
        setTimeout(refresh, 60);
      });
    });

    // Also catch the drawer opening (in case reviews weren't measurable before).
    var drawer = document.getElementById('cart-drawer');
    if (drawer) {
      new MutationObserver(function () {
        setTimeout(function () { initReviews(document); }, 60);
      }).observe(drawer, { attributes: true, attributeFilter: ['class', 'aria-hidden', 'open'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
